import React, { useEffect, useRef, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { FaMagnifyingGlass } from "react-icons/fa6";
import { RiArrowDownSLine } from "react-icons/ri";
import WRGGHeaderLogo from "./WRGGHeaderLogo";
import BetaBadge from "./BetaBadge";
import SearchSuggestionDropdown from "./search/SearchSuggestionDropdown";
import SearchValidationToast from "./search/SearchValidationToast";
import { DISPLAY_REGION_TO_PLATFORM } from "../helper_functions/searchRouting";
import { scopedPlatformOptions, useDeploymentCapabilities } from "../context/DeploymentCapabilitiesContext";

const NAV_LINKS = [
    { label: "Home", to: "/" },
    { label: "Champions", to: "/analytics/champions" },
    { label: "Mastery", to: "/analytics/mastery" },
    { label: "Tier List", to: "/analytics/tier-list" },
    { label: "Ladders", to: "ladders" },
];

const PLATFORM_OPTIONS = [
    { value: "", label: "--", disabled: true },
    { value: "na1", label: "NA" },
    { value: "euw1", label: "EUW" },
    { value: "br1", label: "BR" },
    { value: "eun1", label: "EUNE" },
    { value: "kr", label: "KR" },
    { value: "oc1", label: "OCE" },
    { value: "la1", label: "LAN" },
    { value: "la2", label: "LAS" },
    { value: "ru", label: "RU" },
    { value: "tr1", label: "TR" },
    { value: "me1", label: "ME" },
    { value: "jp1", label: "JP" },
    { value: "sg2", label: "SG" },
    { value: "tw2", label: "TW" },
    { value: "vn2", label: "VN" },
];

const navLinkClassName = ({ isActive }) =>
    [
        "inline-flex min-h-[32px] items-center justify-center border-b px-0.5 py-1 text-[10px] font-semibold uppercase tracking-[0.2em] transition duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500/70 sm:min-h-[34px] sm:text-[11px]",
        isActive
            ? "border-zinc-500 text-zinc-50"
            : "border-transparent text-zinc-300 hover:border-zinc-700 hover:text-zinc-100",
    ].join(" ");

const Header = ({
    formErrors,
    setFormErrors,
    platform,
    displayRegion,
    handleSummonerNameEntry,
    handlePlatformSelection,
    submitAndSearchSummoner,
    summonerSearchFormData,
    championSuggestions,
    recentSearches,
    searchSuggestions,
    searchSuggestionsLoading,
    onSelectChampionAnalytics,
    onSelectChampionPlayers,
    onSelectRecentSearch,
    onSelectSearchSuggestion,
    onSubmitDirectSearch,
    onViewAllSearchResults,
}) => {
    const deploymentCapabilities = useDeploymentCapabilities();
    const scopedOptions = scopedPlatformOptions(PLATFORM_OPTIONS, deploymentCapabilities);
    const location = useLocation();
    const hideSearchBar = location.pathname === "/";
    const routeRegionMatch = location.pathname.match(/^\/(?:search|summoners|ladder)\/([^/]+)/i);
    const routeDisplayRegion = routeRegionMatch?.[1]?.toLowerCase() || "";
    const routePlatform = DISPLAY_REGION_TO_PLATFORM[routeDisplayRegion] || "";
    const selectedPlatform = routePlatform || platform || "";
    const selectedDisplayRegion = routeDisplayRegion || displayRegion || "na";
    // const showTaglineHint = !summonerSearchFormData?.gameName && !summonerSearchFormData?.tagLine;
    const [isCompactSearch, setIsCompactSearch] = useState(() =>
        typeof window !== "undefined" ? window.innerWidth < 640 : false
    );
    const [showSuggestions, setShowSuggestions] = useState(false);
    const searchContainerRef = useRef(null);

    useEffect(() => {
        if (typeof window === "undefined") return undefined;

        const mediaQuery = window.matchMedia("(max-width: 639px)");
        const syncCompactSearch = (event) => setIsCompactSearch(event.matches);

        setIsCompactSearch(mediaQuery.matches);

        if (typeof mediaQuery.addEventListener === "function") {
            mediaQuery.addEventListener("change", syncCompactSearch);
            return () => mediaQuery.removeEventListener("change", syncCompactSearch);
        }

        mediaQuery.addListener(syncCompactSearch);
        return () => mediaQuery.removeListener(syncCompactSearch);
    }, []);

    useEffect(() => {
        if (!showSuggestions) {
            return undefined;
        }

        function handlePointerDown(event) {
            const clickedInsideDropdown = event.target instanceof Element
                && Boolean(event.target.closest('[data-search-suggestion-dropdown="true"]'));

            if (!searchContainerRef.current?.contains(event.target) && !clickedInsideDropdown) {
                setShowSuggestions(false);
            }
        }

        document.addEventListener('pointerdown', handlePointerDown, true);
        return () => document.removeEventListener('pointerdown', handlePointerDown, true);
    }, [showSuggestions]);

    useEffect(() => {
        setShowSuggestions(false);
    }, [location.pathname, location.search]);

    return (
        <header className="border-b border-zinc-800/80 bg-zinc-950/95 shadow-[0_1px_0_rgba(255,255,255,0.03)] backdrop-blur-xl">
            <div className="mx-auto max-w-screen-lg">
                <div className="mx-4 py-2.5 custom-lg:mx-0 lg:py-3">
                    <div className={hideSearchBar ? "flex items-center justify-end" : "flex flex-col gap-2.5 lg:grid lg:grid-cols-[auto_minmax(380px,1fr)] lg:items-center lg:gap-x-5 lg:gap-y-2"}>
                        {hideSearchBar ? null : (
                            <div className="flex items-center gap-2 lg:contents">
                                <div className="flex min-w-0 items-center justify-between gap-3 lg:min-w-0">
                                    <NavLink
                                        to="/"
                                        className="group inline-flex items-center gap-1 text-zinc-100 transition duration-200 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500/70 sm:gap-2"
                                    >
                                        <WRGGHeaderLogo className="h-6 w-auto transition duration-200 group-hover:scale-[1.03] sm:h-8" />
                                        <BetaBadge className="hidden shrink-0 sm:inline-flex" />
                                    </NavLink>
                                </div>

                                <form
                                    className="min-w-0 flex-1 lg:w-full lg:min-w-0 lg:max-w-[500px]"
                                    onSubmit={(event) => {
                                        setShowSuggestions(false);
                                        submitAndSearchSummoner(event);
                                    }}
                                >
                                    <div ref={searchContainerRef} className="relative z-[1400]">
                                    <div className="group flex min-h-[42px] items-center rounded-full border border-zinc-700/85 bg-zinc-900/72 pr-1 shadow-[inset_0_1px_0_rgba(255,255,255,0.03)] transition duration-200 hover:border-zinc-600 focus-within:border-cyan-700/80 focus-within:shadow-[0_0_0_1px_rgba(8,145,178,0.24)] min-[420px]:pr-1.5">
                                        <div className="relative flex h-full w-[56px] shrink-0 items-center pl-1 min-[420px]:w-[70px] min-[420px]:pl-1.5">
                                            <select
                                                aria-label="Region"
                                                className="h-8 w-full appearance-none rounded-full border border-transparent bg-transparent pl-2 pr-4 text-[10px] font-semibold uppercase tracking-[0.16em] text-zinc-200 transition duration-200 hover:text-zinc-100 focus:outline-none min-[420px]:pl-3 min-[420px]:pr-6 min-[420px]:tracking-[0.2em]"
                                                value={selectedPlatform}
                                                onChange={(event) => handlePlatformSelection(event)}
                                                name="platform"
                                            >
                                                {scopedOptions.map((option) => (
                                                    <option key={option.value || "placeholder"} value={option.value} disabled={option.disabled}>
                                                        {option.label}
                                                    </option>
                                                ))}
                                            </select>
                                            <RiArrowDownSLine className="pointer-events-none absolute right-1 text-[13px] text-zinc-300 transition duration-200 group-focus-within:text-cyan-300 min-[420px]:right-2.5 min-[420px]:text-base" />
                                        </div>

                                        <div className="mx-1 h-4 w-px shrink-0 bg-zinc-700/90 min-[420px]:mx-2" />

                                        <div className="relative flex min-w-0 flex-1 items-center pr-0.5 min-[420px]:pr-1">
                                            <input
                                                autoComplete="off"
                                                autoCorrect="off"
                                                spellCheck="false"
                                                aria-label="Search summoner"
                                                className="min-w-0 w-full bg-transparent px-1 text-[12px] text-zinc-200 placeholder:text-[11px] placeholder:font-normal placeholder:italic placeholder:text-zinc-400 focus:outline-none min-[420px]:text-[15px] min-[420px]:placeholder:text-[13px]"
                                                onChange={handleSummonerNameEntry}
                                                onInput={handleSummonerNameEntry}
                                                onFocus={() => setShowSuggestions(true)}
                                                onKeyDown={(event) => {
                                                    if (event.key === "Enter") {
                                                        setShowSuggestions(false);
                                                    }
                                                }}
                                                placeholder={isCompactSearch ? "Search Summoner" : "Search Summoner + #TagLine"}
                                                name="gameName"
                                                type="text"
                                                value={summonerSearchFormData?.query || ""}
                                            />
                                            {/* {showTaglineHint && !isCompactSearch ? (
                                                <span className="pointer-events-none absolute right-0 top-1/2 -translate-y-1/2 rounded-md border border-zinc-800/80 bg-zinc-950/88 px-1.5 py-[2px] text-[10px] font-normal tracking-[0.03em] text-zinc-400 shadow-[inset_0_1px_0_rgba(255,255,255,0.03)] min-[420px]:right-0 min-[420px]:px-2 min-[420px]:py-[3px] min-[420px]:text-[11px]">
                                                    #TagLine
                                                </span>
                                            ) : null} */}
                                        </div>

                                        <button
                                            aria-label="Search"
                                            className="ml-0.5 flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-full text-zinc-300 transition duration-200 hover:bg-zinc-800/80 hover:text-zinc-100 active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500/70 min-[420px]:ml-1 min-[420px]:h-8 min-[420px]:w-8"
                                            type="submit"
                                        >
                                            <FaMagnifyingGlass className="h-3 w-3 min-[420px]:h-3.5 min-[420px]:w-3.5" />
                                        </button>
                                    </div>
                                    {showSuggestions ? (
                                        <SearchSuggestionDropdown
                                            anchorRef={searchContainerRef}
                                            championResults={championSuggestions}
                                            hasSelectedRegion={Boolean(selectedPlatform)}
                                            loading={searchSuggestionsLoading}
                                            query={summonerSearchFormData?.query || ""}
                                            recentSearches={recentSearches}
                                            results={searchSuggestions}
                                            onSelectChampionAnalytics={(champion) => {
                                                setShowSuggestions(false)
                                                onSelectChampionAnalytics(champion)
                                            }}
                                            onSelectChampionPlayers={(champion) => {
                                                setShowSuggestions(false)
                                                onSelectChampionPlayers(champion)
                                            }}
                                            onSelectRecentSearch={(recentSearch) => {
                                                setShowSuggestions(false)
                                                onSelectRecentSearch(recentSearch)
                                            }}
                                            onSelectSummoner={(result) => {
                                                setShowSuggestions(false)
                                                onSelectSearchSuggestion(result)
                                            }}
                                            onSubmitDirectSearch={() => {
                                                setShowSuggestions(false)
                                                onSubmitDirectSearch()
                                            }}
                                            onViewAllResults={() => {
                                                if (!(summonerSearchFormData?.query || '').trim()) {
                                                    return
                                                }
                                                setShowSuggestions(false)
                                                onViewAllSearchResults()
                                            }}
                                        />
                                    ) : null}
                                    {formErrors?.length > 0 ? (
                                        <SearchValidationToast
                                            messages={formErrors}
                                            onDismiss={() => setFormErrors([])}
                                        />
                                    ) : null}
                                    </div>
                                </form>
                            </div>
                        )}

                        <nav className="flex flex-wrap items-center justify-start gap-x-4 gap-y-1 sm:gap-x-5 lg:col-span-2 lg:justify-start">
                            {NAV_LINKS.map((link) => (
                                <NavLink
                                    key={link.label}
                                    className={navLinkClassName}
                                    to={link.to === "ladders" ? `/ladder/${selectedDisplayRegion}/solo/1` : link.to}
                                >
                                    {link.label}
                                </NavLink>
                            ))}
                            <BetaBadge className="ml-auto sm:hidden" />
                        </nav>
                    </div>

                </div>
            </div>
        </header>
    );
};

export default Header;
